import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminLayout from "../layouts/AdminLayout";
import API from "../api/api";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaUsers,
  FaUserTie,
  FaSuitcase,
  FaSyncAlt
} from "react-icons/fa";

export default function Dashboard() {
  const navigate = useNavigate();

  const storedUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user");

  const user = storedUser
    ? JSON.parse(storedUser)
    : null;

  const firstName =
    user?.first_name ||
    user?.firstName ||
    user?.name?.split(" ")?.[0] ||
    "there";

  const [overview, setOverview] = useState({
    bookings: 0,
    revenue: 0,
    occupancy: 0
  });

  const [operations, setOperations] = useState({
    today: [],
    upcoming: [],
    stats: {
      todayTours: 0,
      todayGuests: 0,
      todayBookings: 0,
      needsStaffing: 0,
      staffed: 0,
      staffingPercent: 0
    }
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const todayLabel = useMemo(() => {
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    }).format(new Date());
  }, []);

  const loadDashboard = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError("");

      const [overviewResponse, operationsResponse] =
        await Promise.all([
          API.get("/admin/dashboard/overview"),
          API.get("/admin/dashboard/operations")
        ]);

      setOverview(
        overviewResponse.data || {
          bookings: 0,
          revenue: 0,
          occupancy: 0
        }
      );

      setOperations(
        operationsResponse.data || {
          today: [],
          upcoming: [],
          stats: {
            todayTours: 0,
            todayGuests: 0,
            todayBookings: 0,
            needsStaffing: 0,
            staffed: 0,
            staffingPercent: 0
          }
        }
      );
    } catch (err) {
      console.error("Dashboard load error:", err);

      setError(
        err.response?.data?.error ||
        "Unable to load dashboard data."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard(true);
  }, []);

  const formatTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  const formatShortDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short"
    }).format(date);
  };

  const getInitials = (first, last) => {
    const a = first?.[0] || "";
    const b = last?.[0] || "";

    return `${a}${b}`.toUpperCase() || "?";
  };

  const getGuideName = (tour) => {
    if (!tour.guide_id) {
      return "Needs staffing";
    }

    return (
      `${tour.guide_first_name || ""} ${
        tour.guide_last_name || ""
      }`.trim() || "Assigned"
    );
  };

  const stats = operations.stats || {};

  const staffingPercent = Math.max(
    0,
    Math.min(
      100,
      Number(stats.staffingPercent || 0)
    )
  );

  const todayTours = operations.today || [];
  const upcomingTours = operations.upcoming || [];

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.loadingPage}>
          <div style={styles.spinner} />
          <p>Loading dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="omni-dashboard">
        <style>{responsiveStyles}</style>

        {/* =====================================================
            HEADER
        ===================================================== */}

        <header className="dashboard-header">
          <div>
            <div className="eyebrow">
              ADMINISTRATION
            </div>

            <h1 className="dashboard-title">
              Good morning, {firstName}
            </h1>

            <p className="dashboard-subtitle">
              Here's what's happening with your tours today.
            </p>
          </div>

          <div className="header-actions">
            <div className="date-pill">
              <FaCalendarAlt />
              <span>{todayLabel}</span>
            </div>

            <button
              className="refresh-button"
              onClick={() => loadDashboard(false)}
              disabled={refreshing}
              title="Refresh dashboard"
            >
              <FaSyncAlt
                className={
                  refreshing ? "spin-icon" : ""
                }
              />
            </button>
          </div>
        </header>

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <div className="error-banner">
            <FaExclamationTriangle />

            <div>
              <strong>Dashboard data unavailable</strong>
              <span>{error}</span>
            </div>

            <button
              onClick={() => loadDashboard(false)}
            >
              Retry
            </button>
          </div>
        )}

        {/* =====================================================
            KPI CARDS
        ===================================================== */}

        <section className="stats-grid">

          <StatCard
            icon={<FaSuitcase />}
            label="Tours today"
            value={stats.todayTours || 0}
            helper={
              stats.needsStaffing > 0
                ? `${stats.needsStaffing} need staffing`
                : "Everything staffed"
            }
            accent={
              stats.needsStaffing > 0
                ? "warning"
                : "success"
            }
          />

          <StatCard
            icon={<FaUsers />}
            label="Guests today"
            value={stats.todayGuests || 0}
            helper={
              stats.todayBookings
                ? `${stats.todayBookings} bookings`
                : "No bookings yet"
            }
            accent="blue"
          />

          <StatCard
            icon={<FaUserTie />}
            label="Staffing"
            value={`${staffingPercent}%`}
            helper={
              stats.needsStaffing > 0
                ? `${stats.needsStaffing} tours unassigned`
                : "All tours assigned"
            }
            accent={
              staffingPercent === 100
                ? "success"
                : "warning"
            }
          />

          <StatCard
            icon={<FaCheckCircle />}
            label="Total bookings"
            value={overview.bookings || 0}
            helper="All time"
            accent="purple"
          />

        </section>

        {/* =====================================================
            MAIN GRID
        ===================================================== */}

        <section className="main-grid">

          {/* ===================================================
              TODAY'S SCHEDULE
          =================================================== */}

          <div className="panel schedule-panel">

            <div className="panel-header">
              <div>
                <h2>Today's schedule</h2>
                <p>
                  Your tours and staffing status for today.
                </p>
              </div>

              <button
                className="text-button"
                onClick={() =>
                  navigate("/admin/tours")
                }
              >
                View all
                <FaArrowRight />
              </button>
            </div>

            {todayTours.length === 0 ? (
              <EmptyState
                icon={<FaCalendarAlt />}
                title="No tours scheduled today"
                text="There are no scheduled tours for today."
              />
            ) : (
              <div className="schedule-list">

                {todayTours.map((tour) => {
                  const staffed = Boolean(
                    tour.guide_id
                  );

                  return (
                    <div
                      key={tour.id}
                      className="schedule-row"
                    >

                      <div className="time-column">
                        <strong>
                          {formatTime(
                            tour.start_datetime
                          )}
                        </strong>

                        <span>
                          {formatTime(
                            tour.end_datetime
                          )}
                        </span>
                      </div>

                      <div className="timeline">
                        <div
                          className={
                            staffed
                              ? "timeline-dot staffed"
                              : "timeline-dot unstaffed"
                          }
                        />
                      </div>

                      <div className="tour-information">

                        <div className="tour-title-row">
                          <h3>
                            {tour.title}
                          </h3>

                          <StatusBadge
                            staffed={staffed}
                          />
                        </div>

                        <div className="tour-meta">

                          {tour.location && (
                            <span>
                              <FaMapMarkerAlt />
                              {tour.location}
                            </span>
                          )}

                          <span>
                            <FaUsers />
                            {Number(
                              tour.booked || 0
                            )} guests
                          </span>

                          {tour.capacity && (
                            <span>
                              Capacity {tour.capacity}
                            </span>
                          )}

                        </div>

                        <div className="guide-row">

                          {staffed ? (
                            <>
                              <div className="avatar">
                                {getInitials(
                                  tour.guide_first_name,
                                  tour.guide_last_name
                                )}
                              </div>

                              <span>
                                {getGuideName(tour)}
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="avatar avatar-warning">
                                <FaUserTie />
                              </div>

                              <span className="warning-text">
                                Guide needed
                              </span>

                              <button
                                className="assign-button"
                                onClick={() =>
                                  navigate(
                                    "/admin/guides"
                                  )
                                }
                              >
                                Assign guide
                              </button>
                            </>
                          )}

                        </div>

                      </div>

                      <button
                        className="row-arrow"
                        onClick={() =>
                          navigate(
                            `/admin/tour/${tour.id}`
                          )
                        }
                        aria-label="Open tour"
                      >
                        <FaArrowRight />
                      </button>

                    </div>
                  );
                })}

              </div>
            )}

          </div>

          {/* ===================================================
              STAFFING
          =================================================== */}

          <div className="panel staffing-panel">

            <div className="panel-header">
              <div>
                <h2>Staffing</h2>
                <p>
                  Today's guide coverage.
                </p>
              </div>

              <FaUserTie className="panel-icon" />
            </div>

            <div className="staffing-number">
              {staffingPercent}%
            </div>

            <p className="staffing-description">
              {stats.needsStaffing > 0
                ? `${stats.needsStaffing} ${
                    stats.needsStaffing === 1
                      ? "tour needs"
                      : "tours need"
                  } a guide.`
                : "Every tour today has a guide assigned."}
            </p>

            <div className="progress-track">
              <div
                className={
                  staffingPercent === 100
                    ? "progress-fill complete"
                    : "progress-fill"
                }
                style={{
                  width: `${staffingPercent}%`
                }}
              />
            </div>

            <div className="progress-labels">
              <span>
                {stats.staffed || 0} staffed
              </span>

              <span>
                {stats.needsStaffing || 0} unassigned
              </span>
            </div>

            {stats.needsStaffing > 0 && (
              <button
                className="primary-action"
                onClick={() =>
                  navigate("/admin/guides")
                }
              >
                Resolve staffing
                <FaArrowRight />
              </button>
            )}

            {stats.needsStaffing === 0 && (
              <div className="success-message">
                <FaCheckCircle />
                Staffing is complete
              </div>
            )}

          </div>

        </section>

        {/* =====================================================
            NEEDS ATTENTION
        ===================================================== */}

        {stats.needsStaffing > 0 && (
          <section className="attention-panel">

            <div className="attention-icon">
              <FaExclamationTriangle />
            </div>

            <div className="attention-content">
              <strong>
                {stats.needsStaffing} tour
                {stats.needsStaffing === 1
                  ? ""
                  : "s"} need staffing
              </strong>

              <span>
                Assign guides before the tours begin.
              </span>
            </div>

            <button
              onClick={() =>
                navigate("/admin/guides")
              }
            >
              Review tours
              <FaArrowRight />
            </button>

          </section>
        )}

        {/* =====================================================
            UPCOMING TOURS
        ===================================================== */}

        <section className="panel upcoming-panel">

          <div className="panel-header">
            <div>
              <h2>Upcoming tours</h2>
              <p>
                Your next scheduled departures.
              </p>
            </div>

            <button
              className="text-button"
              onClick={() =>
                navigate("/admin/tours")
              }
            >
              View all
              <FaArrowRight />
            </button>
          </div>

          {upcomingTours.length === 0 ? (
            <EmptyState
              icon={<FaClock />}
              title="No upcoming tours"
              text="There are no upcoming scheduled tours."
            />
          ) : (
            <div className="upcoming-grid">

              {upcomingTours
                .slice(0, 6)
                .map((tour) => {
                  const staffed = Boolean(
                    tour.guide_id
                  );

                  return (
                    <div
                      key={tour.id}
                      className="upcoming-card"
                      onClick={() =>
                        navigate(
                          `/admin/tour/${tour.id}`
                        )
                      }
                    >

                      <div className="upcoming-date">
                        <span>
                          {formatShortDate(
                            tour.start_datetime
                          )}
                        </span>

                        <strong>
                          {formatTime(
                            tour.start_datetime
                          )}
                        </strong>
                      </div>

                      <div className="upcoming-body">

                        <h3>
                          {tour.title}
                        </h3>

                        <div className="upcoming-guests">
                          <FaUsers />

                          <span>
                            {Number(
                              tour.booked || 0
                            )} guests
                          </span>

                          {tour.capacity && (
                            <span>
                              / {tour.capacity}
                            </span>
                          )}
                        </div>

                        <StatusBadge
                          staffed={staffed}
                        />

                      </div>

                      <FaArrowRight className="upcoming-arrow" />

                    </div>
                  );
                })}

            </div>
          )}

        </section>

        {/* =====================================================
            QUICK ACTIONS
        ===================================================== */}

        <section className="quick-actions">

          <QuickAction
            icon={<FaSuitcase />}
            title="Manage tours"
            text="Create, edit and schedule tours."
            onClick={() =>
              navigate("/admin/tours")
            }
          />

          <QuickAction
            icon={<FaUsers />}
            title="Manage bookings"
            text="Review guests and reservations."
            onClick={() =>
              navigate("/admin/bookings")
            }
          />

          <QuickAction
            icon={<FaUserTie />}
            title="Manage guides"
            text="Assign guides and check availability."
            onClick={() =>
              navigate("/admin/guides")
            }
          />

        </section>

      </div>
    </AdminLayout>
  );
}


/* ============================================================
   COMPONENTS
============================================================ */

function StatCard({
  icon,
  label,
  value,
  helper,
  accent = "blue"
}) {
  return (
    <div className="stat-card">

      <div
        className={`stat-icon ${accent}`}
      >
        {icon}
      </div>

      <div className="stat-content">
        <span className="stat-label">
          {label}
        </span>

        <strong className="stat-value">
          {value}
        </strong>

        <span
          className={
            accent === "warning"
              ? "stat-helper warning"
              : "stat-helper"
          }
        >
          {helper}
        </span>
      </div>

    </div>
  );
}


function StatusBadge({ staffed }) {
  return (
    <span
      className={
        staffed
          ? "status-badge staffed-badge"
          : "status-badge unstaffed-badge"
      }
    >
      {staffed ? (
        <>
          <FaCheckCircle />
          Staffed
        </>
      ) : (
        <>
          <FaExclamationTriangle />
          Needs staffing
        </>
      )}
    </span>
  );
}


function EmptyState({
  icon,
  title,
  text
}) {
  return (
    <div className="empty-state">

      <div className="empty-icon">
        {icon}
      </div>

      <strong>{title}</strong>

      <span>{text}</span>

    </div>
  );
}


function QuickAction({
  icon,
  title,
  text,
  onClick
}) {
  return (
    <button
      className="quick-action"
      onClick={onClick}
    >
      <div className="quick-action-icon">
        {icon}
      </div>

      <div>
        <strong>{title}</strong>
        <span>{text}</span>
      </div>

      <FaArrowRight />
    </button>
  );
}


/* ============================================================
   INLINE STYLES
============================================================ */

const styles = {
  loadingPage: {
    minHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    gap: 14
  },

  spinner: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "3px solid #e2e8f0",
    borderTopColor: "#c75028",
    animation: "omniSpin 0.8s linear infinite"
  }
};


/* ============================================================
   RESPONSIVE CSS
============================================================ */

const responsiveStyles = `
@keyframes omniSpin {
  to {
    transform: rotate(360deg);
  }
}

.omni-dashboard {
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  color: #172033;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.dashboard-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 28px;
}

.eyebrow {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.6px;
  color: #c75028;
  margin-bottom: 8px;
}

.dashboard-title {
  margin: 0;
  font-size: 34px;
  line-height: 1.1;
  font-weight: 750;
  letter-spacing: -1px;
  color: #172033;
}

.dashboard-subtitle {
  margin: 9px 0 0;
  color: #718096;
  font-size: 15px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-pill {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 15px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e7e9ed;
  color: #596273;
  font-size: 13px;
  white-space: nowrap;
}

.date-pill svg {
  color: #c75028;
}

.refresh-button {
  width: 42px;
  height: 42px;
  border: 1px solid #e7e9ed;
  border-radius: 12px;
  background: #ffffff;
  color: #596273;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refresh-button:hover {
  background: #f8fafc;
}

.refresh-button:disabled {
  opacity: .6;
  cursor: default;
}

.spin-icon {
  animation: omniSpin .8s linear infinite;
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 22px;
  padding: 14px 16px;
  border: 1px solid #f3c8b7;
  background: #fff7f3;
  border-radius: 14px;
  color: #8b3f24;
}

.error-banner > svg {
  flex-shrink: 0;
}

.error-banner div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.error-banner span {
  font-size: 13px;
}

.error-banner button {
  border: 0;
  background: #c75028;
  color: white;
  border-radius: 8px;
  padding: 8px 13px;
  cursor: pointer;
  font-weight: 650;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

.stat-card {
  min-height: 142px;
  background: #ffffff;
  border: 1px solid #e7e9ed;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  box-sizing: border-box;
}

.stat-icon {
  width: 43px;
  height: 43px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.blue {
  background: #edf5ff;
  color: #3977c9;
}

.stat-icon.success {
  background: #ecf9f1;
  color: #24935c;
}

.stat-icon.warning {
  background: #fff5e8;
  color: #d27a14;
}

.stat-icon.purple {
  background: #f2efff;
  color: #735bc7;
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  font-weight: 650;
  color: #7a8494;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 28px;
  line-height: 1.1;
  font-weight: 750;
  letter-spacing: -0.7px;
  color: #172033;
}

.stat-helper {
  margin-top: 8px;
  font-size: 12px;
  color: #8992a0;
}

.stat-helper.warning {
  color: #c2761c;
  font-weight: 650;
}

.main-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(300px, .8fr);
  gap: 18px;
  margin-bottom: 18px;
}

.panel {
  background: #ffffff;
  border: 1px solid #e7e9ed;
  border-radius: 17px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 21px;
  border-bottom: 1px solid #eef0f3;
}

.panel-header h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 720;
  color: #172033;
}

.panel-header p {
  margin: 5px 0 0;
  font-size: 12px;
  color: #8a93a0;
}

.text-button {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 0;
  background: transparent;
  color: #c75028;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.text-button:hover {
  text-decoration: underline;
}

.schedule-list {
  padding: 2px 21px 8px;
}

.schedule-row {
  min-height: 105px;
  display: grid;
  grid-template-columns: 65px 20px minmax(0, 1fr) 28px;
  gap: 12px;
  align-items: center;
  border-bottom: 1px solid #f0f1f3;
}

.schedule-row:last-child {
  border-bottom: 0;
}

.time-column {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

.time-column strong {
  font-size: 14px;
  color: #293244;
}

.time-column span {
  font-size: 11px;
  color: #9aa1ad;
}

.timeline {
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.timeline::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e8ebef;
}

.timeline-dot {
  position: relative;
  z-index: 1;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #24935c;
  box-shadow: 0 0 0 4px #ecf9f1;
}

.timeline-dot.unstaffed {
  background: #d27a14;
  box-shadow: 0 0 0 4px #fff5e8;
}

.tour-information {
  min-width: 0;
  padding: 15px 0;
}

.tour-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tour-title-row h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #293244;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 750;
  white-space: nowrap;
}

.staffed-badge {
  background: #ecf9f1;
  color: #258153;
}

.unstaffed-badge {
  background: #fff5e8;
  color: #b66c16;
}

.tour-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  color: #8b94a1;
  font-size: 11px;
  flex-wrap: wrap;
}

.tour-meta span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.tour-meta svg {
  font-size: 10px;
}

.guide-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: #606a79;
  font-size: 11px;
}

.avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #edf1f5;
  color: #536071;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 750;
}

.avatar-warning {
  background: #fff5e8;
  color: #c1781b;
}

.warning-text {
  color: #b66c16;
  font-weight: 650;
}

.assign-button {
  margin-left: 5px;
  border: 0;
  background: transparent;
  color: #c75028;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.row-arrow {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #a0a7b1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
}

.row-arrow:hover {
  background: #f5f6f8;
  color: #c75028;
}

.staffing-panel {
  padding-bottom: 20px;
}

.panel-icon {
  color: #c75028;
  font-size: 18px;
}

.staffing-number {
  padding: 26px 21px 0;
  font-size: 46px;
  line-height: 1;
  font-weight: 780;
  letter-spacing: -2px;
  color: #172033;
}

.staffing-description {
  padding: 0 21px;
  margin: 10px 0 22px;
  color: #7e8795;
  font-size: 13px;
  line-height: 1.5;
}

.progress-track {
  margin: 0 21px;
  height: 9px;
  border-radius: 999px;
  background: #edf0f3;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #c75028;
  transition: width .35s ease;
}

.progress-fill.complete {
  background: #24935c;
}

.progress-labels {
  padding: 8px 21px 0;
  display: flex;
  justify-content: space-between;
  color: #9299a4;
  font-size: 11px;
}

.primary-action {
  margin: 24px 21px 0;
  width: calc(100% - 42px);
  min-height: 42px;
  border: 0;
  border-radius: 10px;
  background: #c75028;
  color: white;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.primary-action:hover {
  background: #b54520;
}

.success-message {
  margin: 24px 21px 0;
  min-height: 42px;
  border-radius: 10px;
  background: #ecf9f1;
  color: #258153;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
}

.attention-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 17px;
  margin-bottom: 18px;
  background: #fff9ef;
  border: 1px solid #f3dfbd;
  border-radius: 14px;
}

.attention-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fff0d4;
  color: #c1781b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.attention-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
}

.attention-content strong {
  font-size: 13px;
  color: #81501a;
}

.attention-content span {
  font-size: 11px;
  color: #9a7849;
}

.attention-panel button {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 0;
  background: transparent;
  color: #b66c16;
  font-size: 11px;
  font-weight: 750;
  cursor: pointer;
}

.upcoming-panel {
  margin-bottom: 18px;
}

.upcoming-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: #eef0f3;
}

.upcoming-card {
  background: white;
  min-height: 112px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
  transition: background .15s ease;
}

.upcoming-card:hover {
  background: #fafafa;
}

.upcoming-date {
  min-width: 74px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.upcoming-date span {
  color: #8a93a0;
  font-size: 10px;
  font-weight: 650;
  text-transform: uppercase;
}

.upcoming-date strong {
  color: #293244;
  font-size: 16px;
}

.upcoming-body {
  min-width: 0;
  flex: 1;
}

.upcoming-body h3 {
  margin: 0 0 7px;
  font-size: 13px;
  font-weight: 700;
  color: #293244;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.upcoming-guests {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 9px;
  color: #8a93a0;
  font-size: 10px;
}

.upcoming-guests svg {
  color: #9da5af;
}

.upcoming-arrow {
  color: #a6adb6;
  font-size: 11px;
  flex-shrink: 0;
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  padding-bottom: 30px;
}

.quick-action {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  min-width: 0;
  text-align: left;
  border: 1px solid #e7e9ed;
  background: #ffffff;
  border-radius: 14px;
  cursor: pointer;
}

.quick-action:hover {
  border-color: #d8dce2;
  background: #fcfcfc;
}

.quick-action-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fff2ec;
  color: #c75028;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-action div:nth-child(2) {
  min-width: 0;
  flex: 1;
}

.quick-action strong,
.quick-action span {
  display: block;
}

.quick-action strong {
  font-size: 12px;
  color: #293244;
  margin-bottom: 4px;
}

.quick-action span {
  font-size: 10px;
  line-height: 1.4;
  color: #8a93a0;
}

.quick-action > svg {
  color: #a4abb4;
  font-size: 10px;
}

.empty-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #8b94a1;
  padding: 20px;
  text-align: center;
}

.empty-state strong {
  color: #5f6876;
  font-size: 13px;
}

.empty-state span {
  font-size: 11px;
}

.empty-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #f4f5f7;
  color: #a1a8b2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .main-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .dashboard-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .date-pill {
    flex: 1;
  }

  .dashboard-title {
    font-size: 28px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .schedule-row {
    grid-template-columns: 52px 15px minmax(0, 1fr);
  }

  .row-arrow {
    display: none;
  }

  .upcoming-grid {
    grid-template-columns: 1fr;
  }

  .quick-actions {
    grid-template-columns: 1fr;
  }

  .tour-meta {
    gap: 8px;
  }
}

@media (max-width: 500px) {
  .dashboard-title {
    font-size: 25px;
  }

  .panel-header {
    padding: 17px;
  }

  .schedule-list {
    padding-left: 15px;
    padding-right: 15px;
  }

  .schedule-row {
    grid-template-columns: 48px 12px minmax(0, 1fr);
    gap: 8px;
  }

  .tour-title-row h3 {
    font-size: 13px;
  }

  .date-pill span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
`;